/**
 * shading — register a PDF axial/radial SHADING (+ its Function) for a gradient
 * HATCH (PDF-6.5b), mirroring `extGState.ts`'s Resources-subdict register
 * pattern.
 *
 * AutoCAD gradient hatches (LINEAR / CYLINDER / SPHERICAL / …) carry a colour
 * ramp that the prior slice (PDF-6.5a) added to the typed `HatchEntity.gradient`
 * model. This module projects that ramp onto a REAL PDF shading so the gradient
 * plots with smooth colour interpolation instead of a flat solid fallback:
 *
 *   - ShadingType 2 (axial)  → LINEAR / CYLINDER / INVCYLINDER / CURVED / INVCURVED
 *   - ShadingType 3 (radial) → SPHERICAL / INVSPHERICAL / HEMISPHERICAL / INVHEMISPHERICAL
 *
 * The shading's colour ramp is a /Function:
 *   - a plain 2-colour ramp        → FunctionType 2 (exponential, N = 1);
 *   - CURVED / INVCURVED           → FunctionType 2 with N = the easing exponent
 *                                    (an AutoCAD-appearance approximation, see
 *                                    {@link CURVED_EASING_N});
 *   - CYLINDER / INVCYLINDER       → FunctionType 3 (stitching) of two Type-2
 *                                    subfunctions [c0→c1, c1→c0] so the highlight
 *                                    runs down the middle of the axis.
 *
 * # PDF spec reference
 *   - PDF 32000-1:2008 §8.7.4.5.3 "Type 2 (Axial) Shadings"
 *   - PDF 32000-1:2008 §8.7.4.5.4 "Type 3 (Radial) Shadings"
 *   - PDF 32000-1:2008 §7.10.3   "Type 2 (Exponential Interpolation) Functions"
 *   - PDF 32000-1:2008 §7.10.4   "Type 3 (Stitching) Functions"
 *
 * # API
 *
 *   const shName = ensureShading(page, {
 *     shadingType: "axial",
 *     coords: [x0, y0, x1, y1],        // already in pt
 *     color0: { r, g, b }, color1: { r, g, b },
 *     symmetric: false, inverse: false, curved: false,
 *   });
 *   page.pushOperators(
 *     PDFOperator.of(PDFOperatorNames.ShadingFill, [PDFName.of(shName)]),
 *   );
 *
 * Unlike `ensureExtGState`, shadings are NOT dedup'd: a gradient hatch is rare
 * and each carries its own boundary-derived coords, so two distinct gradients
 * would (almost) never share a key. We instead register a fresh `/Shading` dict
 * per call under a UNIQUE per-page resource name (`Sh<counter>`) so multiple
 * gradient hatches on one page never collide.
 */

import {
  PDFArray,
  PDFBool,
  PDFDict,
  PDFName,
  PDFNumber,
  type PDFContext,
  type PDFPage,
} from "pdf-lib";

import type { RgbColor } from "./pdfTransform";

/**
 * CURVED / INVCURVED easing exponent for the FunctionType 2 `/N`.
 *
 * Re-exported from the SHARED gradient-ramp module (scene3d/hatch/gradientRamp —
 * the single source of truth across the PDF + scene render pipes; imported UP,
 * like `getPatternDefinition`). Kept exported here so this module's existing
 * importers (and tests) need no path change. PDF FunctionType 2's exponential
 * interpolation `C(t) = C0 + t^N · (C1 − C0)` produces the eased ramp for
 * `N ≠ 1`; `N = 2` is the documented AutoCAD-APPEARANCE approximation — see the
 * shared module's JSDoc for the full rationale.
 */
export { CURVED_EASING_N } from "../../scene3d/hatch/gradientRamp";
import { CURVED_EASING_N } from "../../scene3d/hatch/gradientRamp";

/** A resolved shading registration request. */
export interface ShadingSpec {
  /** Axial (ShadingType 2) or radial (ShadingType 3). */
  shadingType: "axial" | "radial";
  /**
   * Geometry coordinates ALREADY in pt:
   *   - axial  → `[x0, y0, x1, y1]` (axis endpoints);
   *   - radial → `[cx, cy, r0, cx, cy, r1]` (inner disc 0 → outer disc r1).
   */
  coords: number[];
  /** Ramp end-0 plot RGB (oneColor/tint + INV swap already folded in). */
  color0: RgbColor;
  /** Ramp end-1 plot RGB (oneColor/tint + INV swap already folded in). */
  color1: RgbColor;
  /**
   * SYMMETRIC ramp (CYLINDER / INVCYLINDER): the function is the stitched
   * c0→c1→c0 so the highlight is in the middle. False = a plain c0→c1 ramp.
   */
  symmetric: boolean;
  /**
   * INV* gradient: the colour order was swapped by the converter into
   * color0/color1. Carried so the symmetric stitching encode can stay
   * order-faithful (the two halves already reflect the swapped endpoints).
   */
  inverse: boolean;
  /** CURVED / INVCURVED: use the eased FunctionType 2 (`N = CURVED_EASING_N`). */
  curved: boolean;
}

/** Per-page counter so each registration gets a unique `Sh<n>` resource name. */
const PAGE_SHADING_COUNTER = new WeakMap<PDFPage, { n: number }>();

/** Build a PDFArray of PDFNumbers from a number list. */
function numberArray(context: PDFContext, values: number[]): PDFArray {
  const arr = PDFArray.withContext(context);
  for (const v of values) arr.push(PDFNumber.of(v));
  return arr;
}

/** Build a 3-component DeviceRGB colour array `[r g b]`. */
function colorArray(context: PDFContext, c: RgbColor): PDFArray {
  return numberArray(context, [c.r, c.g, c.b]);
}

/**
 * Build a FunctionType 2 (exponential) ramp dict `c0 → c1` over `[0, 1]`.
 * `N = 1` is a straight linear ramp; `N = CURVED_EASING_N` eases it.
 */
function buildExponentialFunction(
  context: PDFContext,
  c0: RgbColor,
  c1: RgbColor,
  n: number,
): PDFDict {
  const dict = PDFDict.withContext(context);
  dict.set(PDFName.of("FunctionType"), PDFNumber.of(2));
  dict.set(PDFName.of("Domain"), numberArray(context, [0, 1]));
  dict.set(PDFName.of("C0"), colorArray(context, c0));
  dict.set(PDFName.of("C1"), colorArray(context, c1));
  dict.set(PDFName.of("N"), PDFNumber.of(n));
  return dict;
}

/**
 * Build the colour-ramp /Function for `spec`:
 *   - symmetric → FunctionType 3 stitching of [c0→c1, c1→c0] with
 *     `/Bounds [0.5] /Encode [0 1 0 1] /Domain [0 1]` (the highlight is in the
 *     middle — CYLINDER);
 *   - curved    → FunctionType 2 with `N = CURVED_EASING_N` (eased ramp);
 *   - else      → FunctionType 2 with `N = 1` (straight linear ramp).
 *
 * The INV* swap is ALREADY folded into color0/color1 by the converter, so this
 * builder always reads `spec.color0 → spec.color1` directly (the symmetric
 * stitching mirrors that ordered pair into c0→c1→c0).
 */
function buildFunction(context: PDFContext, spec: ShadingSpec): PDFDict {
  if (spec.symmetric) {
    // Two Type-2 halves: [c0 → c1] then [c1 → c0] (highlight in the middle).
    const half0 = buildExponentialFunction(context, spec.color0, spec.color1, 1);
    const half1 = buildExponentialFunction(context, spec.color1, spec.color0, 1);
    const fns = PDFArray.withContext(context);
    fns.push(context.register(half0));
    fns.push(context.register(half1));

    const dict = PDFDict.withContext(context);
    dict.set(PDFName.of("FunctionType"), PDFNumber.of(3));
    dict.set(PDFName.of("Domain"), numberArray(context, [0, 1]));
    dict.set(PDFName.of("Functions"), fns);
    // Split the [0,1] domain at 0.5 — first half c0→c1, second half c1→c0.
    dict.set(PDFName.of("Bounds"), numberArray(context, [0.5]));
    // Each subfunction's own [0,1] domain maps to the full [0,1] sub-input.
    dict.set(PDFName.of("Encode"), numberArray(context, [0, 1, 0, 1]));
    return dict;
  }
  const n = spec.curved ? CURVED_EASING_N : 1;
  return buildExponentialFunction(context, spec.color0, spec.color1, n);
}

/**
 * Build the /Shading dict for `spec`, referencing the registered /Function.
 *   - axial  → ShadingType 2, `/Coords [x0 y0 x1 y1]`;
 *   - radial → ShadingType 3, `/Coords [cx cy r0 cx cy r1]`.
 * Both carry `/ColorSpace /DeviceRGB` and `/Extend [true true]` so the fill
 * covers the whole clipped region beyond the axis/disc endpoints.
 */
function buildShading(
  context: PDFContext,
  spec: ShadingSpec,
  functionRef: ReturnType<PDFContext["register"]>,
): PDFDict {
  const dict = PDFDict.withContext(context);
  dict.set(
    PDFName.of("ShadingType"),
    PDFNumber.of(spec.shadingType === "axial" ? 2 : 3),
  );
  dict.set(PDFName.of("ColorSpace"), PDFName.of("DeviceRGB"));
  dict.set(PDFName.of("Coords"), numberArray(context, spec.coords));
  dict.set(PDFName.of("Function"), functionRef);
  const extend = PDFArray.withContext(context);
  extend.push(PDFBool.True);
  extend.push(PDFBool.True);
  dict.set(PDFName.of("Extend"), extend);
  return dict;
}

/**
 * Register a /Shading (+ its /Function) on `page` and attach it under a unique
 * `Sh<n>` name in the page Resources `/Shading` subdict, returning that name.
 * Feed the returned name to an `sh` operator
 * (`PDFOperator.of(PDFOperatorNames.ShadingFill, [PDFName.of(name)])`).
 */
export function ensureShading(page: PDFPage, spec: ShadingSpec): string {
  const context = page.doc.context;

  // Per-page unique resource name (`Sh0`, `Sh1`, …) — no dedup.
  let counter = PAGE_SHADING_COUNTER.get(page);
  if (!counter) {
    counter = { n: 0 };
    PAGE_SHADING_COUNTER.set(page, counter);
  }
  const name = `Sh${counter.n}`;
  counter.n += 1;

  const functionDict = buildFunction(context, spec);
  const functionRef = context.register(functionDict);
  const shadingDict = buildShading(context, spec, functionRef);
  const shadingRef = context.register(shadingDict);

  // Attach to the page's Resources `/Shading` subdictionary (mirrors the
  // ExtGState attach in `extGState.ts`). Defend against an absent Resources
  // dict for structural robustness.
  let resources = page.node.Resources();
  if (!resources) {
    resources = context.obj({});
    page.node.set(PDFName.of("Resources"), resources);
  }
  const found = resources.lookupMaybe(PDFName.of("Shading"), PDFDict);
  let shadingResources: PDFDict;
  if (found instanceof PDFDict) {
    shadingResources = found;
  } else {
    shadingResources = context.obj({});
    resources.set(PDFName.of("Shading"), shadingResources);
  }
  shadingResources.set(PDFName.of(name), shadingRef);

  return name;
}
